/**
 * ステップ見積もり -超松竹梅- のドメインモデル（純関数）。
 *
 * 項目表（EstimateItemRow の配列）から:
 *  - `_a` / `_b` サフィックスによるグループ化 → ステップ列の構築
 *  - 単価の解決（数値 / 他項目参照 / 表示文字列）
 *  - トグルステップ判定（2 択かつ一方が単価 0）
 *  - ウィザード選択状態からの明細行・合計計算
 * を行う。UI にも BE 送信にも依存しない層なのでユニットテストで仕様を固定する
 * （estimateModel.test.ts）。
 */

import { CONSUMPTION_TAX_RATE } from "../../../../utils/tax";

// ===== 行データ =====

export interface EstimateItemRow {
  /** 行の一意 ID（並び替え・選択状態のキー）。 */
  id: string;
  /** 項目名。末尾 `_a` `_b` … で同一グループの選択肢になる。 */
  name: string;
  /**
   * 単価（文字列で保持）。
   *  - 数値として読める（"50000" / "¥50,000" / 全角数字）→ 金額計算に含む
   *  - 他項目の項目名（完全名 or グループのベース名）→ その項目の単価を参照
   *  - それ以外（"一式" / "別途お見積り" 等）→ 表示のみ・計算外
   */
  unitPrice: string;
  /** 既定数量。単位が "%" のときはパーセント値として扱う。 */
  quantity: number;
  /**
   * 単位（"曲" "式" "回" 等）。
   * "%" を指定した行は数量をパーセント値とみなし、金額を単価の百分率で出す。
   * 単価に他項目名を書く参照と組み合わせると「参照先の 10%」を表現できる。
   */
  unit: string;
  /** 備考（PDF の備考欄にそのまま出る）。 */
  note: string;
  /** 数量可変フラグ。ON のときウィザードでステッパー表示。 */
  quantityVariable: boolean;
  /** 数量可変時の下限。 */
  quantityMin: number;
  /** 数量可変時の上限。 */
  quantityMax: number;
  /** 数量可変時の初期値。 */
  quantityDefault: number;
  /** 項目説明テキスト（ステップ全体の説明として表示）。 */
  itemDescription: string;
  /** 項目サムネイル URL（ステップ全体の画像として表示）。 */
  itemThumbnailUrl: string;
  /** オプションテキスト（この選択肢カード内に表示）。 */
  optionText: string;
  /** オプションサムネイル URL（この選択肢カード内に表示）。 */
  optionThumbnailUrl: string;
}

// ===== グループ化 =====

/** `名前_a` 形式のサフィックス（英数 1 文字）を検出する。 */
const GROUP_SUFFIX_RE = /^(.+)_([A-Za-z0-9])$/;

export interface ParsedRowName {
  /** サフィックスを除いたベース名（サフィックス無しなら name そのまま）。 */
  base: string;
  /** グループサフィックス（無ければ null）。 */
  suffix: string | null;
}

export const parseRowName = (name: string): ParsedRowName => {
  const m = GROUP_SUFFIX_RE.exec(name.trim());
  if (!m) return { base: name.trim(), suffix: null };
  return { base: m[1], suffix: m[2] };
};

// ===== ステップ =====

export interface EstimateStep {
  /** ステップの一意キー（グループは `group:<ベース名>`、単独行は `row:<行id>`）。 */
  key: string;
  /** 表示タイトル（ベース名）。 */
  title: string;
  /** このステップの選択肢（単独行は 1 件）。表の並び順を維持する。 */
  rows: EstimateItemRow[];
  /** 複数選択肢（グループ）か。 */
  isChoice: boolean;
  /** 2 択かつ一方だけが単価 0 → トグル表示。 */
  isToggle: boolean;
  /** トグル時: 単価 0 の行（OFF 側）。トグルでなければ null。 */
  toggleOffRow: EstimateItemRow | null;
  /** トグル時: 単価が付いている行（ON 側）。トグルでなければ null。 */
  toggleOnRow: EstimateItemRow | null;
  /** ステップ全体の説明（グループ内で最初の非空 itemDescription）。 */
  description: string;
  /** ステップ全体のサムネイル（グループ内で最初の非空 itemThumbnailUrl）。 */
  thumbnailUrl: string;
}

// ===== 単価の解決 =====

export type ResolvedUnitPrice =
  | { kind: "number"; value: number }
  | { kind: "text"; text: string };

/** 全角数字・記号を半角へ寄せ、通貨記号 / 桁区切り / 空白を除去する。 */
const normalizeNumericText = (raw: string): string =>
  raw
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/[．]/g, ".")
    .replace(/[¥￥,，]/g, "")
    .replace(/\s/g, "");

/**
 * 単価文字列を数値として読む。読めなければ null。
 * 空文字は 0 円として扱う（トグルの OFF 側などで空欄にしても成立させる）。
 */
export const parseNumericPrice = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === "") return 0;
  const normalized = normalizeNumericText(trimmed);
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
};

/** ウィザードの選択状態。 */
export interface WizardSelectionState {
  /** ステップキー → 選択中の行 id。 */
  selected: Record<string, string>;
  /** 行 id → 数量（数量可変行の現在値）。 */
  quantities: Record<string, number>;
}

interface RowIndex {
  /** 完全一致名 → 行（同名複数は先勝ち）。 */
  byExactName: Map<string, EstimateItemRow>;
  /** グループベース名 → グループ行配列。 */
  byBase: Map<string, EstimateItemRow[]>;
}

const buildRowIndex = (rows: EstimateItemRow[]): RowIndex => {
  const byExactName = new Map<string, EstimateItemRow>();
  const byBase = new Map<string, EstimateItemRow[]>();
  for (const row of rows) {
    const name = row.name.trim();
    if (name && !byExactName.has(name)) byExactName.set(name, row);
    const { base, suffix } = parseRowName(row.name);
    if (suffix !== null) {
      const list = byBase.get(base) ?? [];
      list.push(row);
      byBase.set(base, list);
    }
  }
  return { byExactName, byBase };
};

const stepKeyForBase = (base: string): string => `group:${base}`;

/**
 * 単価を解決する。
 *
 * 優先順: 数値 → 完全一致の項目名参照 → グループベース名参照（そのグループで
 * 選択中の行。選択が無ければ先頭行）→ 表示文字列。
 * 参照の循環は表示文字列（入力そのまま）へフォールバックする。
 */
export const resolveUnitPrice = (
  row: EstimateItemRow,
  rows: EstimateItemRow[],
  selection?: WizardSelectionState,
  index?: RowIndex,
  visiting?: Set<string>,
): ResolvedUnitPrice => {
  const idx = index ?? buildRowIndex(rows);
  const seen = visiting ?? new Set<string>();

  if (seen.has(row.id)) {
    // 循環参照 → 入力文字列をそのまま表示扱いにする
    return { kind: "text", text: row.unitPrice.trim() };
  }
  seen.add(row.id);

  const raw = row.unitPrice.trim();
  const numeric = parseNumericPrice(raw);
  if (numeric !== null) return { kind: "number", value: numeric };

  // 完全一致参照（自分自身は除く）
  const exact = idx.byExactName.get(raw);
  if (exact && exact.id !== row.id) {
    const resolved = resolveUnitPrice(exact, rows, selection, idx, seen);
    if (resolved.kind === "number") return resolved;
    return { kind: "text", text: raw };
  }

  // グループベース名参照（選択中の行 or 先頭行）
  const group = idx.byBase.get(raw);
  if (group && group.length > 0) {
    const selectedId = selection?.selected[stepKeyForBase(raw)];
    const target =
      group.find((r) => r.id === selectedId && r.id !== row.id) ??
      group.find((r) => r.id !== row.id);
    if (target) {
      const resolved = resolveUnitPrice(target, rows, selection, idx, seen);
      if (resolved.kind === "number") return resolved;
    }
    return { kind: "text", text: raw };
  }

  return { kind: "text", text: raw };
};

// ===== 金額計算 =====

/** 単位が百分率（半角 "%" / 全角 "％"）か。 */
export const isPercentUnit = (unit: string): boolean => {
  const trimmed = unit.trim();
  return trimmed === "%" || trimmed === "％";
};

/**
 * 明細 1 行の金額。
 *
 * 通常は「単価 × 数量」。単位が "%" の行は数量をパーセント値とみなして
 * 「単価 × 数量 ÷ 100」で計算する。単価に他項目名を書いた参照行と
 * 組み合わせると「参照先の単価の 10%」といった歩合項目を表現できる。
 * 端数は消費税と同じく切り捨て。
 */
export const calcLineAmount = (
  unitPrice: number,
  quantity: number,
  unit: string,
): number =>
  isPercentUnit(unit)
    ? Math.floor((unitPrice * quantity) / 100)
    : unitPrice * quantity;

// ===== ステップ構築 =====

const firstNonEmpty = (
  rows: EstimateItemRow[],
  // eslint-disable-next-line no-unused-vars
  pick: (row: EstimateItemRow) => string,
): string => {
  for (const row of rows) {
    const v = pick(row).trim();
    if (v) return v;
  }
  return "";
};

/**
 * 表の行列からステップ列を構築する。
 *
 * - サフィックス付き行はベース名でグループ化し 1 ステップ（選択肢）。
 *   ステップの位置はグループの初出行の位置。選択肢の順序は表の並び順。
 * - サフィックス無し行は単独ステップ（常に見積もりに含まれる項目）。
 * - 名前が空の行は無視する。
 */
export const buildSteps = (rows: EstimateItemRow[]): EstimateStep[] => {
  const steps: EstimateStep[] = [];
  const groupSteps = new Map<string, EstimateStep>();

  for (const row of rows) {
    if (!row.name.trim()) continue;
    const { base, suffix } = parseRowName(row.name);

    if (suffix === null) {
      steps.push({
        key: `row:${row.id}`,
        title: base,
        rows: [row],
        isChoice: false,
        isToggle: false,
        toggleOffRow: null,
        toggleOnRow: null,
        description: row.itemDescription.trim(),
        thumbnailUrl: row.itemThumbnailUrl.trim(),
      });
      continue;
    }

    const existing = groupSteps.get(base);
    if (existing) {
      existing.rows.push(row);
    } else {
      const step: EstimateStep = {
        key: stepKeyForBase(base),
        title: base,
        rows: [row],
        isChoice: false,
        isToggle: false,
        toggleOffRow: null,
        toggleOnRow: null,
        description: "",
        thumbnailUrl: "",
      };
      groupSteps.set(base, step);
      steps.push(step);
    }
  }

  // グループの後処理: choice / toggle 判定・ステップ全体の説明とサムネ
  const allRows = rows;
  for (const step of steps) {
    if (step.rows.length > 1) {
      step.isChoice = true;
      step.description = firstNonEmpty(step.rows, (r) => r.itemDescription);
      step.thumbnailUrl = firstNonEmpty(step.rows, (r) => r.itemThumbnailUrl);

      if (step.rows.length === 2) {
        const resolved = step.rows.map((r) => resolveUnitPrice(r, allRows));
        const zeroFlags = resolved.map(
          (p) => p.kind === "number" && p.value === 0,
        );
        // ちょうど一方だけが 0 円のときトグル（両方 0 は通常の 2 択のまま）
        if (zeroFlags[0] !== zeroFlags[1]) {
          step.isToggle = true;
          step.toggleOffRow = zeroFlags[0] ? step.rows[0] : step.rows[1];
          step.toggleOnRow = zeroFlags[0] ? step.rows[1] : step.rows[0];
        }
      }
    }
  }

  return steps;
};

/**
 * ウィザードでユーザーに問う必要があるステップか。
 *
 * 選択肢の無い固定項目は選ぶことが何も無いのでステップからは外し、最終確認の
 * 明細にだけ載せる。ただし数量可変の固定項目は数量の入力が要るため残す。
 * 明細の組み立て（buildEstimateLines）には常に全ステップを渡すこと。
 */
export const isWizardStep = (step: EstimateStep): boolean =>
  step.isChoice || step.rows.some((row) => row.quantityVariable);

// ===== 選択状態 =====

/** 行の数量の初期値（数量可変なら quantityDefault、固定なら quantity）。 */
export const initialQuantityOf = (row: EstimateItemRow): number =>
  row.quantityVariable
    ? clampQuantity(row, row.quantityDefault)
    : row.quantity;

export const clampQuantity = (row: EstimateItemRow, value: number): number => {
  if (!row.quantityVariable) return value;
  const min = Math.min(row.quantityMin, row.quantityMax);
  const max = Math.max(row.quantityMin, row.quantityMax);
  return Math.max(min, Math.min(max, value));
};

/**
 * ステップ列から初期選択状態を作る。
 * 通常の選択肢は先頭行、トグルは OFF（0 円側）を初期選択とする。
 */
export const buildDefaultSelection = (
  steps: EstimateStep[],
): WizardSelectionState => {
  const selected: Record<string, string> = {};
  const quantities: Record<string, number> = {};
  for (const step of steps) {
    if (step.isChoice) {
      selected[step.key] = step.isToggle
        ? (step.toggleOffRow?.id ?? step.rows[0].id)
        : step.rows[0].id;
    }
    for (const row of step.rows) {
      if (row.quantityVariable) {
        quantities[row.id] = initialQuantityOf(row);
      }
    }
  }
  return { selected, quantities };
};

/** ステップで現在選択されている行を返す（fixed ステップは唯一の行）。 */
export const selectedRowOf = (
  step: EstimateStep,
  selection: WizardSelectionState,
): EstimateItemRow => {
  if (!step.isChoice) return step.rows[0];
  const id = selection.selected[step.key];
  return step.rows.find((r) => r.id === id) ?? step.rows[0];
};

// ===== 明細行と合計 =====

export interface EstimateLine {
  rowId: string;
  /** PDF の内容欄（グループはベース名）。 */
  name: string;
  /** 数値単価 or 表示文字列（"一式" 等）。 */
  price: number | string;
  quantity: number;
  unit: string;
  /** 金額（単位 "%" は単価の百分率）。文字列単価は null（計算外）。 */
  amount: number | null;
  note: string;
  /** 選択肢の無い固定項目（ウィザードでは問わず常に含まれる）か。 */
  isFixed: boolean;
}

/**
 * ウィザードの選択状態から見積もり明細を組み立てる。
 *
 * - トグルステップで OFF（0 円側）選択中の項目は明細に含めない。
 * - それ以外は各ステップの選択行（fixed は常に）を 1 行として出力する。
 *
 * `steps` にはウィザードで表示するステップだけでなく、`buildSteps` が返した
 * 全ステップを渡すこと（固定項目もここで明細に載るため）。
 */
export const buildEstimateLines = (
  steps: EstimateStep[],
  rows: EstimateItemRow[],
  selection: WizardSelectionState,
): EstimateLine[] => {
  const index = buildRowIndex(rows);
  const lines: EstimateLine[] = [];

  for (const step of steps) {
    const row = selectedRowOf(step, selection);
    if (step.isToggle && step.toggleOffRow && row.id === step.toggleOffRow.id) {
      continue; // トグル OFF は「含めない」
    }

    const quantity = row.quantityVariable
      ? clampQuantity(row, selection.quantities[row.id] ?? initialQuantityOf(row))
      : row.quantity;
    const resolved = resolveUnitPrice(row, rows, selection, index);

    if (resolved.kind === "number") {
      lines.push({
        rowId: row.id,
        name: step.title,
        price: resolved.value,
        quantity,
        unit: row.unit,
        amount: calcLineAmount(resolved.value, quantity, row.unit),
        note: row.note,
        isFixed: !step.isChoice,
      });
    } else {
      lines.push({
        rowId: row.id,
        name: step.title,
        price: resolved.text,
        quantity,
        unit: row.unit,
        amount: null,
        note: row.note,
        isFixed: !step.isChoice,
      });
    }
  }

  return lines;
};

export interface EstimateTotals {
  /** 課税対象額（数値明細の合計）。 */
  subtotal: number;
  /** 消費税（10%・切り捨て）。 */
  tax: number;
  /** 税込合計。 */
  total: number;
  /** 計算外（文字列単価）の明細を含むか。 */
  hasTextPrice: boolean;
}

export const calcTotals = (lines: EstimateLine[]): EstimateTotals => {
  const subtotal = lines.reduce(
    (sum, line) => sum + (line.amount ?? 0),
    0,
  );
  // BE（step_estimate.py）と同じ「10%・切り捨て」で計算する
  const tax = Math.floor(subtotal * CONSUMPTION_TAX_RATE);
  return {
    subtotal,
    tax,
    total: subtotal + tax,
    hasTextPrice: lines.some((line) => line.amount === null),
  };
};

/** "¥50,000" 形式（文字列単価はそのまま返す）。 */
export const formatPrice = (price: number | string): string =>
  typeof price === "number" ? `¥${price.toLocaleString("ja-JP")}` : price;
